import { useEffect, useState } from "react";

export function useVisibilityState(): "visible" | "hidden" {
  const [state, setState] = useState<"visible" | "hidden">(() => {
    if (typeof document === "undefined") return "visible";
    return (document.visibilityState as "visible" | "hidden") ?? "visible";
  });
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onChange = () =>
      setState((document.visibilityState as "visible" | "hidden") ?? "visible");
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);
  return state;
}

const FAST_ASSET_STATUSES = new Set([
  "uploading",
  "analyzing",
  "analyzing_design",
  "layers_extracted",
  "classifying",
  "ready_to_classify",
]);

const SLOW_ASSET_STATUSES = new Set(["generating", "verifying"]);

// Idle but non-terminal statuses where the asset can still transition
// (e.g. `ready_to_generate` is the staging state before the user clicks
// "Tạo ảnh"; if the user lands on a polling page during this window we
// still want to pick up the eventual flip to `generating` without a
// manual refresh, but at a relaxed cadence so it doesn't hammer the API).
const IDLE_ASSET_STATUSES = new Set([
  "ready_to_generate",
  "verification_blocked",
]);

const TERMINAL_ASSET_STATUSES = new Set([
  "completed",
  "error",
  "classification_failed",
]);

export function pickAssetPollInterval(
  status: string | null | undefined,
  visibility: "visible" | "hidden" = "visible",
): number | false {
  if (visibility === "hidden") return false;
  if (!status) return false;
  if (TERMINAL_ASSET_STATUSES.has(status)) return false;
  if (FAST_ASSET_STATUSES.has(status)) return 1500;
  if (SLOW_ASSET_STATUSES.has(status)) return 2500;
  if (IDLE_ASSET_STATUSES.has(status)) return 15_000;
  return false;
}

export function pickOutputsPollInterval(
  assetStatus: string | null | undefined,
  visibility: "visible" | "hidden" = "visible",
): number | false {
  if (visibility === "hidden") return false;
  if (assetStatus === "generating") return 3000;
  return false;
}

export function pickJobPollInterval(
  jobStatus: string | null | undefined,
  visibility: "visible" | "hidden" = "visible",
): number | false {
  if (visibility === "hidden") return false;
  if (!jobStatus) return 1500;
  if (jobStatus === "completed" || jobStatus === "failed" || jobStatus === "canceled")
    return false;
  return 1500;
}

export function pickBackgroundPollInterval(
  baseMs: number,
  visibility: "visible" | "hidden" = "visible",
): number | false {
  if (visibility === "hidden") return false;
  return baseMs;
}
